<template>
  <div
    :class="['form-element', hidden && 'd-none']"
    :data-test="dataTest"
  >
    <FormLabel
      v-if="label"
      v-bind="{
        label,
        required,
        disabled,
        description,
        descriptionType,
        dirty,
        error,
        id
      }"
    />
    <div v-if="readOnly">
      {{ symbol }} {{ value }}
    </div>
    <div
      v-else
      class="d-flex position-relative"
    >
      <div
        class="position-absolute px-2 align-self-center text-muted"
        style="font-size: 16px; line-height: 16px; padding-top: 1px; left: 2px"
        data-test="symbol"
        @click="focus()"
      >
        {{ symbol }}
      </div>
      <input
        :id="id"
        ref="input"
        type="number"
        :value="value"
        :placeholder="disabled ? '—' : placeholder"
        :disabled="disabled"
        :invalid="invalid && dirty"
        :class="[
          'form-control',
          'pl-4',
          disabled && 'p-0',
        ]"
        v-bind="minMaxStepConfig"
        v-on="inputListners"
      />
    </div>
  </div>
</template>

<script>
import FormLabel from './FormLabel'
import FormMixin from './FormMixin'
import CURRENCIES from '@shared/constants/currencies'

export default {
  mixins: [ FormMixin ],
  components: { FormLabel },
  props: {
    config: {
      type: Object,
      default: () => ({}),
    },
    formValue: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    min () {
      return this.config.hasOwnProperty('min') ? this.config.min : 0
    },
    max () {
      return this.config.hasOwnProperty('max') ? this.config.max : undefined
    },
    step () {
      return this.config.hasOwnProperty('step') ? this.config.step : 0.01
    },
    symbol () {
      if (this.config.symbol?.startsWith('$$')) {
        const referenceKey = this.config.symbol.split('$$')[1]
        return CURRENCIES[this.formValue[referenceKey] || 'USD'].symbol_native || '$'
      } else {
        return this.config.symbol || '$'
      }
    },
    minMaxStepConfig () {
      const { min, max, step } = this
      return {
        ...typeof min === 'number' && !Number.isNaN(min) && { min },
        ...typeof max === 'number' && !Number.isNaN(max) && { max },
        // Because there's no way to turn off HTML5 validation w/o <form> el,
        // we have to use a trick to set step="any" when there's no step config.
        ...typeof step === 'number' && !Number.isNaN(step) ? { step } : { step: 'any' },
      }
    },
    inputListners () {
      return {
        ...this.$listeners,
        blur: () => this.$emit('blur'),
        input: $event => this.update($event.target.value),
        wheel: $event => $event.target.blur(),
      }
    },
  },
  watch: {
    value: {
      immediate: true,
      handler (value, old) {
        if (!old && value && this.$refs.input) this.$refs.input.value = value.toFixed(2)
      },
    },
  },
  methods: {
    update (value) {
      const valueNumber = parseFloat(value)
      if (isNaN(valueNumber)) return this.$emit('input', null)
      const decimalLength = (String(value).split('.')[1] || '').length
      if (decimalLength > 2) return (this.$refs.input.value = value.substring(0, value.length - 1))
      this.$emit('input', valueNumber)
    },
    // Do not remove. This in use by parent components to force instant focus on input
    focus () {
      this.$refs.input.focus()
    },
  },
}
</script>

<style scoped>
.form-control {
  height: 36px;
}
</style>
